import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ChevronRightSmall,
  Filter,
  Plane,
  PlaneLanding,
  CreditCard,
  Hotel,
  Bus,
  Ticket,
  UtensilsCrossed,
  UserCheck,
  Shield,
  Package,
  Users,
  CalendarDays,
  DollarSign,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useSessionState } from '@/hooks/use-session-state';

type EventType = 'TRAVEL_START' | 'TRAVEL_END' | 'PAYMENT_DUE' | 'SERVICE_START' | 'SERVICE_END';
interface CalendarEvent {
  id: string;
  date: string;
  type: EventType;
  bookingId: string;
  bookingReference: string;
  customerName: string;
  packageName: string | null;
  status: string;
  totalPassengers: number;
  totalAmount: number;
  balanceDue: number;
  currency: string;
  travelStartDate: string;
  travelEndDate: string;
  serviceType?: string;
  itemName?: string;
  itemDescription?: string;
}

const eventTypeConfig: Record<EventType, { color: string; dotColor: string; icon: typeof Plane }> =
  {
    TRAVEL_START: {
      color: 'bg-green-100 text-green-700 border-green-200',
      dotColor: 'bg-green-500',
      icon: Plane,
    },
    TRAVEL_END: {
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      dotColor: 'bg-blue-500',
      icon: PlaneLanding,
    },
    SERVICE_START: {
      color: 'bg-purple-100 text-purple-700 border-purple-200',
      dotColor: 'bg-purple-500',
      icon: Ticket,
    },
    SERVICE_END: {
      color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      dotColor: 'bg-indigo-500',
      icon: Ticket,
    },
    PAYMENT_DUE: {
      color: 'bg-orange-100 text-orange-700 border-orange-200',
      dotColor: 'bg-orange-500',
      icon: CreditCard,
    },
  };

const serviceTypeIcons: Record<string, typeof Plane> = {
  ACCOMMODATION: Hotel,
  TRANSPORT: Plane,
  ACTIVITY: Ticket,
  MEAL: UtensilsCrossed,
  GUIDE: UserCheck,
  INSURANCE: Shield,
  TRANSFER: Bus,
  OTHER: Package,
};

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  CONFIRMED: 'bg-blue-100 text-blue-700',
  PARTIALLY_PAID: 'bg-orange-100 text-orange-700',
  FULLY_PAID: 'bg-green-100 text-green-700',
  TICKETED: 'bg-purple-100 text-purple-700',
  IN_PROGRESS: 'bg-cyan-100 text-cyan-700',
  COMPLETED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-100 text-red-700',
  REFUNDED: 'bg-pink-100 text-pink-700',
};

function EventRow({ event }: { event: CalendarEvent }) {
  const { t } = useTranslation('operations');
  const config = eventTypeConfig[event.type];
  const ServiceIcon = event.serviceType
    ? (serviceTypeIcons[event.serviceType] ?? Package)
    : config.icon;

  const isService = event.type === 'SERVICE_START' || event.type === 'SERVICE_END';

  return (
    <Link
      to={`/travel/bookings/${event.bookingId}`}
      className="border-border hover:bg-muted/30 group block border transition-colors"
    >
      {/* Top bar: event type badge */}
      <div
        className={`flex items-center gap-2 border-b px-3 py-1.5 text-xs font-medium ${config.color}`}
      >
        <ServiceIcon className="h-3.5 w-3.5" />
        <span>{t(`eventType.${event.type}`)}</span>
        {isService && event.serviceType && (
          <>
            <span className="opacity-50">·</span>
            <span className="opacity-75">
              {t(`serviceType.${event.serviceType}`, event.serviceType)}
            </span>
          </>
        )}
      </div>

      <div className="space-y-2 p-3">
        {/* Customer & status row */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium">{event.customerName}</span>
          <span
            className={`shrink-0 px-1.5 py-0.5 text-[10px] font-medium ${statusColors[event.status] ?? ''}`}
          >
            {event.status}
          </span>
        </div>

        {/* Service item name + description (for service events) */}
        {isService && event.itemName && (
          <div className="text-sm">
            <span className="text-foreground/80 font-medium">{event.itemName}</span>
            {event.itemDescription && (
              <span className="text-muted-foreground ml-1">— {event.itemDescription}</span>
            )}
          </div>
        )}

        {/* Details grid */}
        <div className="text-muted-foreground grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <div className="flex items-center gap-1.5">
            <CalendarDays className="h-3 w-3 shrink-0" />
            <span>
              {event.travelStartDate} → {event.travelEndDate}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Users className="h-3 w-3 shrink-0" />
            <span>
              {event.totalPassengers} {t('passengers')}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Package className="h-3 w-3 shrink-0" />
            <span className="truncate">{event.packageName ?? '—'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <DollarSign className="h-3 w-3 shrink-0" />
            <span>
              {formatCurrency(event.totalAmount)}
              {event.balanceDue > 0 && (
                <span className="ml-1 text-red-500">({formatCurrency(event.balanceDue)} due)</span>
              )}
            </span>
          </div>
        </div>

        {/* Footer: ref + navigate hint */}
        <div className="border-border/50 flex items-center justify-between border-t pt-1">
          <span className="text-muted-foreground font-mono text-[11px]">
            {event.bookingReference}
          </span>
          <span className="text-primary flex items-center gap-1 text-[11px] opacity-0 transition-opacity group-hover:opacity-100">
            {t('viewBooking')} <ChevronRightSmall className="h-3 w-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export function OperationsCalendarPage() {
  const { t } = useTranslation('operations');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEventTypes, setSelectedEventTypes] = useSessionState<EventType[]>(
    'travel:operations-calendar:selectedEventTypes',
    []
  );
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [view, setView] = useSessionState<'month' | 'week'>(
    'travel:operations-calendar:view',
    'month'
  );

  const dateRange = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (view === 'month') {
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDay = firstDay.getDay();
      const start = new Date(firstDay);
      start.setDate(start.getDate() - startDay);
      const endDay = lastDay.getDay();
      const end = new Date(lastDay);
      end.setDate(end.getDate() + (6 - endDay));
      return { start, end };
    } else {
      const day = currentDate.getDay();
      const start = new Date(currentDate);
      start.setDate(start.getDate() - day);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { start, end };
    }
  }, [currentDate, view]);

  const { data: events, isLoading } = trpc.booking.calendarEvents.useQuery({
    startDate: dateRange.start.toISOString(),
    endDate: dateRange.end.toISOString(),
  });

  const filteredEvents = useMemo(() => {
    if (!events) return [];
    if (selectedEventTypes.length === 0) return events;
    return events.filter((e) => selectedEventTypes.includes(e.type));
  }, [events, selectedEventTypes]);

  const eventsByDate = useMemo(() => {
    const grouped: Record<string, CalendarEvent[]> = {};
    for (const event of filteredEvents) {
      if (!grouped[event.date]) {
        grouped[event.date] = [];
      }
      grouped[event.date]!.push(event);
    }
    return grouped;
  }, [filteredEvents]);

  const calendarDays = useMemo(() => {
    const days: Date[] = [];
    const current = new Date(dateRange.start);
    while (current <= dateRange.end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  }, [dateRange]);

  const navigateMonth = (direction: number) => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else {
      newDate.setDate(newDate.getDate() + direction * 7);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const toggleEventType = (type: EventType) => {
    setSelectedEventTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const monthName = currentDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const weekDays = [
    t('weekDays.sun'),
    t('weekDays.mon'),
    t('weekDays.tue'),
    t('weekDays.wed'),
    t('weekDays.thu'),
    t('weekDays.fri'),
    t('weekDays.sat'),
  ];

  const selectedDayEvents = selectedDay ? (eventsByDate[selectedDay] ?? []) : [];
  const selectedDayFormatted = selectedDay
    ? new Date(selectedDay + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('calendar')}</h1>
          <p className="text-muted-foreground">{t('calendarSubtitle')}</p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8" onClick={goToToday}>
            {t('today')}
          </Button>
          <div className="border-border flex items-center border bg-white">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigateMonth(-1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-4 text-sm font-medium">{monthName}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigateMonth(1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border-border flex flex-wrap items-center gap-4 border bg-white p-4">
        <div className="flex items-center gap-2">
          <Filter className="text-muted-foreground h-4 w-4" />
          <span className="text-sm font-medium">{t('filterByType')}:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {(
            [
              'TRAVEL_START',
              'TRAVEL_END',
              'SERVICE_START',
              'SERVICE_END',
              'PAYMENT_DUE',
            ] as EventType[]
          ).map((type) => {
            const isSelected = selectedEventTypes.includes(type);
            const config = eventTypeConfig[type];
            return (
              <button
                key={type}
                onClick={() => toggleEventType(type)}
                className={`h-8 border px-3 text-sm font-medium transition-all ${
                  isSelected
                    ? `${config.color} border-current`
                    : 'border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t(`eventType.${type}`)}
              </button>
            );
          })}
          {selectedEventTypes.length > 0 && (
            <button
              onClick={() => setSelectedEventTypes([])}
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              {t('clearFilters')}
            </button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className="border-border flex border bg-gray-50">
            <button
              onClick={() => setView('month')}
              className={`px-3 py-1.5 text-sm ${
                view === 'month'
                  ? 'text-foreground bg-white'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('month')}
            </button>
            <button
              onClick={() => setView('week')}
              className={`px-3 py-1.5 text-sm ${
                view === 'week'
                  ? 'text-foreground bg-white'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('week')}
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="border-border overflow-hidden border bg-white">
        <div className="grid grid-cols-7 border-b bg-gray-50">
          {weekDays.map((day) => (
            <div
              key={day}
              className="text-muted-foreground px-2 py-3 text-center text-sm font-medium"
            >
              {day}
            </div>
          ))}
        </div>

        {isLoading ? (
          <div className="flex h-96 items-center justify-center">
            <div className="text-center">
              <CalendarIcon className="text-muted-foreground mx-auto h-8 w-8 animate-pulse" />
              <p className="text-muted-foreground mt-2 text-sm">{t('loading')}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {calendarDays.map((day, index) => {
              const dateKey = day.toISOString().split('T')[0] as string;
              const dayEvents = dateKey ? (eventsByDate[dateKey] ?? []) : [];
              const isToday = day.toDateString() === new Date().toDateString();
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const hasEvents = dayEvents.length > 0;

              return (
                <button
                  key={index}
                  onClick={() => hasEvents && setSelectedDay(dateKey)}
                  className={`min-h-[120px] border-b border-r p-2 text-left transition-colors ${
                    !isCurrentMonth ? 'bg-gray-50' : ''
                  } ${index % 7 === 6 ? 'border-r-0' : ''} ${
                    hasEvents ? 'hover:bg-muted/30 cursor-pointer' : 'cursor-default'
                  }`}
                >
                  <div
                    className={`mb-2 flex h-7 w-7 items-center justify-center text-sm ${
                      isToday
                        ? 'bg-primary text-primary-foreground font-bold'
                        : isCurrentMonth
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                    }`}
                  >
                    {day.getDate()}
                  </div>

                  {dayEvents.length > 0 && (
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((event) => {
                        const config = eventTypeConfig[event.type];
                        return (
                          <div
                            key={event.id}
                            className={`flex items-center gap-1.5 truncate border px-1.5 py-0.5 text-[11px] ${config.color}`}
                          >
                            <div
                              className={`h-1.5 w-1.5 shrink-0 rounded-full ${config.dotColor}`}
                            />
                            <span className="truncate">{event.customerName}</span>
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <p className="text-muted-foreground text-center text-[11px]">
                          {t('moreItems', { count: dayEvents.length - 3 })}
                        </p>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
        {(
          [
            'TRAVEL_START',
            'TRAVEL_END',
            'SERVICE_START',
            'SERVICE_END',
            'PAYMENT_DUE',
          ] as EventType[]
        ).map((type) => {
          const config = eventTypeConfig[type];
          return (
            <div key={type} className="flex items-center gap-2">
              <div className={`h-3 w-3 rounded-full ${config.dotColor}`} />
              <span className="text-muted-foreground">{t(`eventType.${type}`)}</span>
            </div>
          );
        })}
      </div>

      {/* Day Detail Dialog */}
      <Dialog open={!!selectedDay} onOpenChange={(open) => !open && setSelectedDay(null)}>
        <DialogContent className="flex max-h-[80vh] max-w-lg flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>{selectedDayFormatted}</DialogTitle>
            <p className="text-muted-foreground text-sm">
              {t('eventsCount', { count: selectedDayEvents.length })}
            </p>
          </DialogHeader>
          <div className="flex-1 space-y-2 overflow-y-auto pr-1">
            {selectedDayEvents.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
            {selectedDayEvents.length === 0 && (
              <p className="text-muted-foreground py-8 text-center text-sm">
                {t('noEventsForDay')}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
